import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useAuthStore } from '../services/auth';

// Get both live and test keys
const stripeLiveKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripeTestKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST;

// Validate Stripe configuration
if (process.env.NODE_ENV === 'production') {
  if (!stripeLiveKey) {
    // REACT_APP_STRIPE_PUBLISHABLE_KEY is required in production
  } else if (!stripeLiveKey.startsWith('pk_live_')) {
    // WARNING: Using non-live Stripe publishable key in production
  } else {
    // Production frontend using live Stripe publishable key
  }
}

if (!stripeLiveKey && !stripeTestKey) {
  // No Stripe publishable keys configured - payment features will be disabled
}

interface StripeContextType {
  stripe: Promise<Stripe | null>;
  isTestMode: boolean;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export const useStripe = () => {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
};

interface StripeProviderProps {
  children: ReactNode;
}

/** Which publishable key applies for a given mode. */
const resolvePublishableKey = (testMode: boolean): string | undefined =>
  testMode && stripeTestKey ? stripeTestKey : stripeLiveKey;

const readTestModeFlag = (): boolean => {
  try {
    return sessionStorage.getItem('stripe_test_mode') === 'true';
  } catch {
    return false;
  }
};

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const { impersonation } = useAuthStore();

  // Resolve the key once up front. Initialising to a placeholder and then
  // swapping it in an effect would remount <Elements> on every page load.
  const [isTestMode, setIsTestMode] = useState(readTestModeFlag);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>(() => {
    const key = resolvePublishableKey(readTestModeFlag());
    return key ? loadStripe(key) : Promise.resolve(null);
  });
  const [elementsKey, setElementsKey] = useState(0); // Key to force Elements remount

  // The key currently backing stripePromise. Held in a ref because this
  // provider sits above <Elements> and must survive its remounts.
  const activeKeyRef = useRef<string | undefined>(resolvePublishableKey(readTestModeFlag()));

  // Initialize Stripe with appropriate key
  useEffect(() => {
    const checkTestMode = () => {
      // Test mode belongs to the session, not to impersonation - a system admin
      // can switch their own session from Settings without impersonating anyone.
      // The server is the authority; TestModeContext keeps this key in step.
      const shouldUseTestMode = readTestModeFlag();
      const keyToUse = resolvePublishableKey(shouldUseTestMode);

      setIsTestMode(shouldUseTestMode);

      // Bail out when the key is unchanged. Bumping elementsKey remounts every
      // child, and TestModeProvider re-dispatches stripe-test-mode-changed as it
      // mounts - so reacting to an event that changed nothing would loop forever
      // and take the whole app with it.
      if (activeKeyRef.current === keyToUse) {
        return;
      }

      activeKeyRef.current = keyToUse;

      // Create new Stripe promise with appropriate key
      setStripePromise(keyToUse ? loadStripe(keyToUse) : Promise.resolve(null));
      setElementsKey(prev => prev + 1); // Force Elements remount to avoid prop change warning
    };

    checkTestMode();

    // Listen for test mode changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'stripe_test_mode') {
        checkTestMode();
      }
    };

    // Listen for custom events (for same-window updates)
    const handleTestModeChange = () => {
      checkTestMode();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('stripe-test-mode-changed', handleTestModeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('stripe-test-mode-changed', handleTestModeChange);
    };
    // Re-evaluate when impersonation starts or stops: TestModeContext clears the
    // flag on exit, and this reloads Stripe.js with the live key to match
  }, [impersonation?.isImpersonating]);

  const value = {
    stripe: stripePromise,
    isTestMode,
  };

  return (
    <StripeContext.Provider value={value}>
      <Elements key={elementsKey} stripe={stripePromise}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
};